<template>
  <span class="hint-callout" dir="rtl">
    <button
      ref="btnRef"
      type="button"
      class="hint-btn"
      :class="{ 'hint-btn-active': isOpen }"
      :aria-label="text"
      aria-haspopup="true"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @mouseenter="open"
      @mouseleave="close"
      @focus="open"
      @blur="close"
      @click.stop.prevent="toggle"
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="11" x2="12" y2="16" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    </button>

    <Teleport to="body">
      <Transition name="hint-pop">
        <div
          v-if="isOpen"
          ref="balloonRef"
          class="hint-balloon"
          :class="`hint-balloon-${placement}`"
          role="tooltip"
          :style="{ top: pos.top + 'px', left: pos.left + 'px' }"
        >
          <span class="hint-balloon-arrow" aria-hidden="true"></span>
          <span class="hint-balloon-text">{{ text }}</span>
        </div>
      </Transition>
    </Teleport>
  </span>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onBeforeUnmount } from 'vue'

const props = defineProps<{ text: string }>()

const isOpen = ref(false)
const btnRef = ref<HTMLElement>()
const balloonRef = ref<HTMLElement>()
const placement = ref<'below' | 'above'>('below')
const pos = reactive({ top: 0, left: 0 })

const updatePosition = () => {
  const btn = btnRef.value
  if (!btn || typeof window === 'undefined') return
  const rect = btn.getBoundingClientRect()
  const bw = balloonRef.value?.offsetWidth ?? 264
  const bh = balloonRef.value?.offsetHeight ?? 120
  const gap = 10

  let left = rect.left + rect.width / 2 - bw / 2
  left = Math.max(10, Math.min(left, window.innerWidth - bw - 10))

  const spaceBelow = window.innerHeight - rect.bottom - gap
  if (spaceBelow >= bh) {
    placement.value = 'below'
    pos.top = rect.bottom + gap
  } else {
    placement.value = 'above'
    pos.top = rect.top - bh - gap
  }
  pos.left = left
}

const open = () => {
  isOpen.value = true
  requestAnimationFrame(updatePosition)
}

const close = () => {
  isOpen.value = false
}

const toggle = () => {
  isOpen.value ? close() : open()
}

const onDocClick = (e: Event) => {
  const target = e.target as Node
  if (!btnRef.value?.contains(target)) close()
}

watch(isOpen, (v) => {
  if (v) {
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    document.addEventListener('click', onDocClick)
  } else {
    window.removeEventListener('scroll', updatePosition, true)
    window.removeEventListener('resize', updatePosition)
    document.removeEventListener('click', onDocClick)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updatePosition, true)
  window.removeEventListener('resize', updatePosition)
  document.removeEventListener('click', onDocClick)
})
</script>

<style lang="scss" scoped>
.hint-callout {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.hint-btn {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid rgba(3, 160, 84, 0.35);
  background: rgba(3, 160, 84, 0.05);
  color: $leaf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover,
  &:focus-visible,
  &.hint-btn-active {
    background: $leaf;
    border-color: $leaf;
    color: #fff;
    box-shadow: 0 3px 10px rgba(3, 160, 84, 0.3);
  }

  &:active {
    transform: scale(0.92);
  }
}

.hint-balloon {
  position: fixed;
  z-index: 9999;
  max-width: 280px;
  padding: 12px 14px;
  background: #1a1a2e;
  color: #fff;
  border-radius: 12px;
  font-size: 0.78rem;
  line-height: 1.8;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.28);

  .hint-balloon-arrow {
    position: absolute;
    width: 10px;
    height: 10px;
    background: inherit;
    transform: rotate(45deg);
  }

  &.hint-balloon-below {
    .hint-balloon-arrow {
      top: -5px;
      left: 50%;
      margin-left: -5px;
    }
  }

  &.hint-balloon-above {
    .hint-balloon-arrow {
      bottom: -5px;
      left: 50%;
      margin-left: -5px;
    }
  }
}

.hint-balloon-text {
  display: block;
}

.hint-pop-enter-active,
.hint-pop-leave-active {
  transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.hint-pop-enter-from,
.hint-pop-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.96);
}
</style>